import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { MAX_RECORDING_SECONDS } from '../model/newPost.constants';

// AAC first: it uploads without a server-side transcode. Opus/webm is the
// fallback creator-api normalizes to m4a on upload.
const MIME_TYPE_PREFERENCE = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
] as const;

const FILE_EXTENSION: Record<string, string> = {
  'audio/mp4': 'm4a',
  'audio/webm': 'webm',
};

// The upload path matches mimeTypes exactly, so the File carries the bare
// container type without codec parameters.
const containerType = (mimeType: string): string =>
  mimeType.split(';')[0].trim();

export const isAudioRecordingSupported = (): boolean =>
  typeof window !== 'undefined' &&
  window.isSecureContext &&
  Boolean(navigator.mediaDevices?.getUserMedia) &&
  typeof MediaRecorder !== 'undefined' &&
  MIME_TYPE_PREFERENCE.some((type) => MediaRecorder.isTypeSupported(type));

const startErrorMessage = (error: unknown): string => {
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
      return 'Microphone access is blocked. Allow it in your browser settings to record audio.';
    }
    if (
      error.name === 'NotFoundError' ||
      error.name === 'OverconstrainedError'
    ) {
      return 'No microphone was found.';
    }
  }
  return 'Could not start recording.';
};

type Options = {
  onRecorded: (file: File) => void;
  maxDurationSec?: number;
};

type AudioRecorder = {
  isRecording: boolean;
  elapsedSec: number;
  start: () => Promise<void>;
  stop: () => void;
  cancel: () => void;
};

export const useAudioRecorder = ({
  onRecorded,
  maxDurationSec = MAX_RECORDING_SECONDS,
}: Options): AudioRecorder => {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedRef = useRef(0);
  const discardRef = useRef(false);
  const pendingRef = useRef(false);
  const disposedRef = useRef(false);
  const onRecordedRef = useRef(onRecorded);
  onRecordedRef.current = onRecorded;

  const releaseCapture = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
  }, []);

  const finishRecording = useCallback(
    (mimeType: string, chunks: Blob[]) => {
      const discarded = discardRef.current;
      releaseCapture();
      setIsRecording(false);
      setElapsedSec(0);
      if (discarded) return;
      if (chunks.length === 0) {
        toast.error('Recording was too short to save.');
        return;
      }
      const type = containerType(mimeType);
      const extension = FILE_EXTENSION[type] ?? 'webm';
      const file = new File(chunks, `voice-note-${Date.now()}.${extension}`, {
        type,
      });
      onRecordedRef.current(file);
    },
    [releaseCapture],
  );

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    discardRef.current = false;
    recorder.stop();
  }, []);

  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    discardRef.current = true;
    recorder.stop();
  }, []);

  const start = useCallback(async () => {
    if (
      pendingRef.current ||
      recorderRef.current ||
      !isAudioRecordingSupported()
    ) {
      return;
    }
    const mimeType = MIME_TYPE_PREFERENCE.find((type) =>
      MediaRecorder.isTypeSupported(type),
    );
    if (!mimeType) return;

    pendingRef.current = true;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      pendingRef.current = false;
      toast.error(startErrorMessage(error));
      return;
    }
    if (disposedRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      pendingRef.current = false;
      return;
    }

    streamRef.current = stream;
    try {
      const recorder = new MediaRecorder(stream, { mimeType });
      const chunks: Blob[] = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      recorder.onstop = () =>
        finishRecording(recorder.mimeType || mimeType, chunks);
      recorder.onerror = () => {
        discardRef.current = true;
        toast.error('Recording failed.');
        if (recorder.state === 'recording') {
          recorder.stop();
        } else {
          releaseCapture();
          setIsRecording(false);
          setElapsedSec(0);
        }
      };

      recorderRef.current = recorder;
      discardRef.current = false;
      elapsedRef.current = 0;
      recorder.start();
      setElapsedSec(0);
      setIsRecording(true);
      intervalRef.current = setInterval(() => {
        elapsedRef.current += 1;
        setElapsedSec(elapsedRef.current);
        if (elapsedRef.current >= maxDurationSec) stop();
      }, 1000);
    } catch (error) {
      releaseCapture();
      toast.error(startErrorMessage(error));
    } finally {
      pendingRef.current = false;
    }
  }, [finishRecording, maxDurationSec, releaseCapture, stop]);

  useEffect(() => {
    // StrictMode runs this cleanup on a simulated unmount while the refs
    // survive the remount, so the flag must be reset in the effect body.
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
      discardRef.current = true;
      const recorder = recorderRef.current;
      if (recorder) {
        recorder.onstop = null;
        recorder.ondataavailable = null;
        recorder.onerror = null;
        if (recorder.state !== 'inactive') recorder.stop();
      }
      releaseCapture();
    };
  }, [releaseCapture]);

  return { isRecording, elapsedSec, start, stop, cancel };
};
